import { graphql } from 'gatsby';
import React, { FC } from 'react';
import { TestimonialQuery } from '../../graphql-types';
import { usePageContext } from '../components/context/pageContext';
import { useWindowSizeContext } from '../components/context/useWindowSizeContext';

import ArticleAuthor from '../components/experience/article/ArticleAuthor';
import BackButton from '../components/experience/article/BackButton';
import { useColor } from '../components/hooks/useColor';
import { SEO } from '../components/seo/Seo';
import { Blocks } from '../components/ui/blocks/Blocks';
import * as styles from './ExperienceTemplate.module.scss';
import RecommendedArticleSection from '../components/experience/RecommendedArticlesSection';
import MoreCard from '../components/experience/MoreCard';
import { Slider } from '../components/experience/Slider';
import { VideoPlayer } from '../components/experience/VideoPlayer';
import { findTranslation } from '../utils/findTranslation';
import { Carousel } from '@mantine/carousel';

import '@mantine/carousel/styles.css';
import { MantineProvider } from '@mantine/core';
import { GatsbyImage } from 'gatsby-plugin-image';
interface Props {
  data: TestimonialQuery;
}

const ExperienceTemplate = ({ data }: Props) => {
  const windowSize = useWindowSizeContext();
  const { lang, defaultLang } = usePageContext();

  const backButtonText = findTranslation(
    data?.sanityExperiencesSection?._rawBack,
    lang,
    defaultLang,
  );
  const item = data?.sanityCommentItem;
  const expertise = findTranslation(
    item?.commentItemRef?._rawPageExpertiseTitle,
    lang,
    defaultLang,
  );
  const text = findTranslation(item?._rawCommentItemText, lang, defaultLang);
  const title = findTranslation(item?._rawCommentItemTitle, lang, defaultLang);
  const authorName = item?.commentItemName;
  const experienceType = item?.experienceType;
  const currentArticleId = item?._id;

  const authorType = findTranslation(
    item?.commentItemAuthorType?._rawProfileTypeName,
    lang,
    defaultLang,
  );
  const authorInfos = findTranslation(item?._rawCommentItemSubtext, lang, defaultLang);
  const authorPhoto = item?.commentItemImg?.asset?.gatsbyImageData;
  const photoPlaceHolder = item?.commentItemImg?.asset?.gatsbyImageData;
  const recommendedArticleTitle = findTranslation(
    data?.sanityExperiencesSection?._rawRecommended,
    lang,
    defaultLang,
  );
  const colorNav = useColor(item?.commentItemRef?.colorNav);

  const pathology = data?.sanityCommentItem?.pathology?.pathology?.find(
    (item) => item?.language === lang,
  )?.text;

  const bodyArea = data?.sanityCommentItem?.bodyArea?.bodyArea?.find(
    (item) => item?.language === lang,
  )?.text;

  data.sanityCommentItem?.pathology?.pathology?.map((pathologyItem) => pathologyItem?.text);

  const photos = item?.photos;

  const Photos: FC = () => {
    if (item?.url) {
      return <VideoPlayer videoUrl={item?.url} />;
    } else {
      if (photos && photos?.length > 0) {
        return (
          <>
            <Carousel
              withIndicators
              withControls
              classNames={{ indicator: styles.indicator, indicators: styles.indicators }}
            >
              {photos?.map((item, index) => (
                <Carousel.Slide
                  style={{ aspectRatio: '16/9', borderRadius: '10px', width: '100%' }}
                >
                  <GatsbyImage
                    image={item?.asset?.gatsbyImageData}
                    style={{ aspectRatio: '16/9' }}
                    objectFit="cover"
                    alt="image"
                  />
                </Carousel.Slide>
              ))}
            </Carousel>
          </>
        );
      } else {
        return <></>;
      }
    }
  };

  return (
    <div className={styles.wrapper}>
      <MantineProvider>
        <SEO
          rawMeta={item?._rawMetadata}
          fallbackTitle={
            findTranslation(item?._rawCommentItemTitle, lang, defaultLang) ??
            findTranslation(item?._rawCommentItemTitle, lang, defaultLang)
          }
          fallbackImage={item?.photos?.[0]?.asset?.url ?? ''}
        />
        <div>
          <BackButton buttonText={backButtonText} />
          <div
          // id="XPfullDisplay"
          // className={s.XPfullDisplay}
          >
            <div className={styles.fullDisplayBox}>
              <div className={styles.header}>
                <div
                  style={{ color: colorNav }}
                  className={styles.expertiseTitle}
                >
                  {expertise}
                </div>
                <div className={styles.type}>{experienceType}</div>
              </div>
              <div className={styles.title}>{title}</div>

              <Photos />
              <div className={styles.text}>
                <Blocks blocks={text} />
              </div>
              <ArticleAuthor
                authorInfos={authorInfos}
                authorName={authorName}
                authorPhoto={authorPhoto}
              />
              <RecommendedArticleSection
                experienceType={experienceType ?? ''}
                pathology={pathology ?? ''}
                bodyArea={bodyArea}
                currentArticleId={currentArticleId}
                sectionTitle={recommendedArticleTitle}
              />
              <BackButton buttonText={backButtonText} />
            </div>
          </div>
          {/*</div>*/}
        </div>
        <div className={styles.cards}>
          <div style={{ marginBottom: '2rem' }}>
            <MoreCard
              title={findTranslation(
                data?.sanityExperiencesSection?._rawArticleCardTitle1,
                lang,
                defaultLang,
              )}
              link={findTranslation(
                data?.sanityExperiencesSection?._rawArticleCardSlug1,
                lang,
                defaultLang,
              )}
              linkLabel={findTranslation(
                data?.sanityExperiencesSection?._rawArticleCardSlugLabel1,
                lang,
                defaultLang,
              )}
              textColor={data?.sanityExperiencesSection?.articleCardTitleColor1?.hex}
              arrowColor={data?.sanityExperiencesSection?.articleCardTitleColor1?.hex}
              overlayColor={data?.sanityExperiencesSection?.articleCardOverlayColor1?.hex}
              imgUrl={data?.sanityExperiencesSection?.articleCardImg1?.asset?.url}
            />
          </div>

          <MoreCard
            title={findTranslation(
              data?.sanityExperiencesSection?._rawArticleCardTitle2,
              lang,
              defaultLang,
            )}
            link={findTranslation(
              data?.sanityExperiencesSection?._rawArticleCardSlug2,
              lang,
              defaultLang,
            )}
            linkLabel={findTranslation(
              data?.sanityExperiencesSection?._rawArticleCardSlugLabel2,
              lang,
              defaultLang,
            )}
            textColor={data?.sanityExperiencesSection?.articleCardTitleColor2?.hex}
            arrowColor={data?.sanityExperiencesSection?.articleCardTitleColor2?.hex}
            overlayColor={data?.sanityExperiencesSection?.articleCardOverlayColor2?.hex}
            imgUrl={data?.sanityExperiencesSection?.articleCardImg2?.asset?.url}
          />
        </div>
      </MantineProvider>
    </div>
  );
};

export const QUERY = graphql`
  query Testimonial($id: String) {
    sanityExperiencesSection {
      _rawRecommended(resolveReferences: { maxDepth: 10 })
      _rawBack(resolveReferences: { maxDepth: 10 })
      _rawArticleCardTitle1(resolveReferences: { maxDepth: 10 })
      _rawArticleCardSlugLabel1(resolveReferences: { maxDepth: 10 })
      _rawArticleCardSlug1(resolveReferences: { maxDepth: 10 })
      articleCardImg1 {
        asset {
          url
        }
      }
      articleCardImg2 {
        asset {
          url
        }
      }
      articleCardTitleColor1 {
        hex
      }
      articleCardOverlayColor1 {
        hex
      }
      _rawArticleCardTitle2(resolveReferences: { maxDepth: 10 })
      _rawArticleCardSlugLabel2(resolveReferences: { maxDepth: 10 })
      _rawArticleCardSlug2(resolveReferences: { maxDepth: 10 })
      articleCardTitleColor2 {
        hex
      }
      articleCardOverlayColor2 {
        hex
      }
    }
    sanityCommentItem(id: { eq: $id }) {
      _rawMetadata(resolveReferences: { maxDepth: 10 })
      id
      _id
      _rawCommentItemTitle(resolveReferences: { maxDepth: 10 })
      _rawCommentItemText(resolveReferences: { maxDepth: 10 })
      _rawCommentItemSubtext(resolveReferences: { maxDepth: 10 })
      _rawCommentItemTextOverview(resolveReferences: { maxDepth: 10 })
      pathology {
        pathology {
          id
          text
          language
        }
      }
      bodyArea {
        bodyArea {
          text
          language
        }
      }
      experienceType
      photos {
        asset {
          gatsbyImageData
          url
        }
      }
      commentItemName
      url
      commentItemRef {
        id
        type
        colorNav
        _rawPageExpertiseTitle(resolveReferences: { maxDepth: 10 })
      }
      commentItemAuthorType {
        _rawProfileTypeName(resolveReferences: { maxDepth: 10 })
        id
      }
      commentItemImg {
        asset {
          gatsbyImageData
        }
      }
    }
  }
`;

export function Head() {
  return (
    <>
      <script
        type="application/ld+json"
        id="gtagManager"
        src="https://www.googletagmanager.com/gtag/js?id=G-CPD641WMLY"
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());

      gtag('config', 'G-CPD641WMLY', {'send_page_view':false});
      gtag('config', 'GTM-KJSJGRLX', {'send_page_view':false});
`,
        }}
      ></script>
    </>
  );
}

export default ExperienceTemplate;
